import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import wifiCellularMismatchClient from "@/apis/wifiCellularMismatchClient";
import IcomoonIcon from "@icons/IcomoonIcon";
import SntDeleteIcon from "@icons/SntDeleteIcon";
import SntDateView from "@/components/ReactBootstrap/SntDateView";
import SntLink from "@/components/ReactBootstrap/SntLink";
import SntText from "@/components/ReactBootstrap/SntText";
import { DefaultTag } from "@cleanup/components/TagMore";
import convert from "convert-units";
import SntPencilIcon from "@icons/SntPencilIcon";
import SntActionButton from "@/cleanup/components/Buttons/SntActionButton";
import SntAdvancedSearchTableViewCard from "../../components/SntTableViewCard/SntAdvancedSearchTableViewCard";
import AdminLayout from "../../containers/AdminLayout/AdminLayout";
import useDeleteAllDialog from "@pages/WifiCellularMismatch/useDeleteAllDialog";
import searchUIClient from "@/apis/searchUIClient";
import { DeviceHomeRoute } from "@/cleanup/utils/routesUtil/routesUtil";
import SntFilterIcon from "@icons/SntFilterIcon";
import SntBulkIcon from "@icons/SntBulkIcon";
import GlobalUtils from "@/utils/GlobalUtils";
import { Button } from "react-bootstrap";
import SntDialog from "@/components/SntDialog/SntDialog";
import moment from "moment";
import UpdateWifiCellularMismatchMap from "./UpdateWifiCellularMismatchMap";
import Loading from "@/components/ReactBootstrap/LoaderSpinner/Loading";
import SntRadio from "@cleanup/components/SntRadio/SntRadio.tsx";
import SntInfoPanel from "@cleanup/components/SntInfo/SntInfoPanel";
import SntCheckBox from "@cleanup/components/SntCheckBox/SntCheckBox";
import { VerticalLayout } from "@/components/ReactBootstrap/FormValidation";

const PAGE_NAME = "WIFI_CELLULAR_MISMATCH";
const DEFAULT_OPTION = {
  pageName: PAGE_NAME,
  queryObjectType: "WIFI_CELLULAR_MISMATCH",
  filtersVisibleAtInit: ["deviceSerial", "cellId", "createdAt"],
  optionsSettings: {},
};

const LocationMismatch = ({ reload }) => {
  //redux state
  const language = useSelector((state) => state.language);
  const table = useRef();
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const [reloadSessionState, setReloadSessionState] = useState(false);
  const loginInfo = useSelector((state) => state.user);
  let { unitDistance, permissionsMap } = loginInfo;

  const [forceFilterAction, setForceFilterAction] = useState(null);
  const [filterMap, setFilterMap] = useState(null);
  const [showResolveAll, setShowResolveAll] = useState(false);

  const [items, setItems] = useState([]);
  const [isShowResponse, setShowResponse] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [decisionOption, setDecisionOption] = useState(1); // Default: delay decision
  const [newCellTowerLocation, setNewCellTowerLocation] = useState(null);

  // Map display options
  const [showWifi, setShowWifi] = useState(true);
  const [showLocationiq, setShowLocationiq] = useState(true);
  const [showOpencell, setShowOpencell] = useState(true);
  const [showHere, setShowHere] = useState(true);
  const [showAdjacentTrustedPoints, setShowAdjacentTrustedPoints] = useState(
    false
  );

  const allColumns = useMemo(
    () => [
      {
        key: "cellId",
        title: language.cell_id,
        Cell: ({ cell, value }) => {
          return <SntText value={value}></SntText>;
        },
      },
      {
        key: "deviceSerial",
        title: language.serial_key,
        Cell: ({ cell, value }) => {
          return value ? (
            <SntLink
              to={DeviceHomeRoute.getUpdatePage({
                serial: value,
              })}
            >
              {value}
            </SntLink>
          ) : (
            ""
          );
        },
      },
      {
        key: "wifiProvider",
        title: language.wifi_provider_key,
        Cell: ({ cell, value }) => {
          return value || "";
        },
      },
      {
        key: "macs",
        title: language.wifi_mac_addresses_key,
        Cell: ({ cell, value }) => {
          return (
            value?.map((m) => (
              <DefaultTag key={m} tagName={m} className="me-1 mb-1" />
            )) || ""
          );
        },
      },
      {
        key: "wifiLocation",
        title: language.journey_wifi_location_key,
        disableSortBy: true,
        Cell: ({ cell, value }) => {
          var full = cell.row.original;
          return full.wifiLat && full.wifiLng ? (
            <>
              <div className="d-flex">
                latitude: {full.wifiLat} &nbsp;
                <a
                  target=" "
                  href={
                    "http://maps.google.com/maps?q=loc:" +
                    full.wifiLat +
                    "," +
                    full.wifiLng
                  }
                >
                  <IcomoonIcon
                    icon="map2"
                    size="12"
                    color="#6EDC85"
                  ></IcomoonIcon>
                </a>
              </div>
              <div>longitude: {full.wifiLng}</div>
            </>
          ) : (
            ""
          );
        },
        cellCopyData: (data) => {
          var text = "";
          if (data.wifiLat != undefined && data.wifiLat != null) {
            text += data.wifiLat + " - ";
          }
          if (data.wifiLng != undefined && data.wifiLng != null) {
            text += data.wifiLng + " - ";
          }
          return [text];
        },
        cellFilterValue: (data) => {
          var text = "";
          if (data.wifiLat != undefined && data.wifiLat != null) {
            text += data.wifiLat + " - ";
          }
          if (data.wifiLng != undefined && data.wifiLng != null) {
            text += data.wifiLng + " - ";
          }
          return [text];
        },
      },
      {
        key: "cellularInfo",
        title: language.cellular_tower_info,
        Cell: ({ cell }) => {
          const full = cell.row.original;
          const info = GlobalUtils.formatCellularTowerInfo(full, "");
          return <div>{info}</div>;
        },
        cellCopyData: (data) => {
          var text = "";
          if (data.cellLat != undefined && data.cellLat != null) {
            text += data.cellLat + " - ";
          }
          if (data.cellLng != undefined && data.cellLng != null) {
            text += data.cellLng + " - ";
          }
          return [text];
        },
        cellFilterValue: (data) => {
          var text = "";
          if (data.cellLat != undefined && data.cellLat != null) {
            text += data.cellLat + " - ";
          }
          if (data.cellLng != undefined && data.cellLng != null) {
            text += data.cellLng + " - ";
          }
          return [text];
        },
      },
      {
        key: "cellularProvider",
        title: language.cellular_provider_key,
        Cell: ({ cell, value }) => {
          var full = cell.row.original;
          return full.network || "";
        },
      },
      {
        key: "cellularLocation",
        title: language.cellular_tower_location,
        disableSortBy: true,
        Cell: ({ cell, value }) => {
          var full = cell.row.original;
          return full.cellLat && full.cellLng ? (
            <>
              <div className="d-flex">
                latitude: {full.cellLat} &nbsp;
                <a
                  target=" "
                  href={
                    "http://maps.google.com/maps?q=loc:" +
                    full.cellLat +
                    "," +
                    full.cellLng
                  }
                >
                  <IcomoonIcon
                    icon="map2"
                    size="12"
                    color="#6EDC85"
                  ></IcomoonIcon>
                </a>
              </div>
              <div>longitude: {full.cellLng}</div>
            </>
          ) : (
            ""
          );
        },
        cellCopyData: (data) => {
          var text = "";
          if (data.cellLat != undefined && data.cellLat != null) {
            text += data.cellLat + " - ";
          }
          if (data.cellLng != undefined && data.cellLng != null) {
            text += data.cellLng + " - ";
          }
          return [text];
        },
        cellFilterValue: (data) => {
          var text = "";
          if (data.cellLat != undefined && data.cellLat != null) {
            text += data.cellLat + " - ";
          }
          if (data.cellLng != undefined && data.cellLng != null) {
            text += data.cellLng + " - ";
          }
          return [text];
        },
      },
      {
        key: "distance",
        title: language.distance_key,
        Cell: ({ cell, value }) => {
          if (value) {
            if (unitDistance === "KILOMETER") {
              return (
                Math.round(convert(value).from("m").to("km")) +
                " " +
                language.maintenance_unit_KM_key
              );
            } else {
              return (
                Math.round(convert(value).from("m").to("mi")) +
                " " +
                language.maintenance_unit_MI_key
              );
            }
          } else {
            return "";
          }
        },
      },
      {
        key: "createdAt",
        title: language.created_date_key,
        Cell: ({ value }) => {
          return (
            <SntDateView
              value={value}
              other={language.na_key}
              format="lll"
            ></SntDateView>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const defaultColumnInit = useMemo(
    () => [
      "deviceSerial",
      "wifiProvider",
      "macs",
      "wifiLocation",
      "cellularInfo",
      "cellularProvider",
      "cellularLocation",
      "distance",
      "createdAt",
    ],
    []
  );

  const extraCols = useMemo(
    () => [
      {
        key: "actions",
        title: language.actions_key,
        immovable: true,
        disableSortBy: true,
        canResize: false,
        Cell: ({ cell }) => {
          var full = cell.row.original;
          return (
            <div>
              {permissionsMap["CELL_TOWER_TROUBLESHOOTING_MANAGE"] && (
                <>
                  <SntActionButton
                    title={language.add_as_filterkey}
                    onClick={(e) => {
                      onFilterClicked(full);
                    }}
                  >
                    <SntFilterIcon />
                  </SntActionButton>

                  <SntActionButton
                    title={language.edit_key}
                    onClick={(e) => {
                      setShowUpdatePopup(true);
                      setSelectedItems([full.id]);
                    }}
                  >
                    <SntPencilIcon />
                  </SntActionButton>

                  <SntActionButton
                    title={language.delete_key}
                    onClick={(e) => onDeleteClicked(e, full)}
                  >
                    <SntDeleteIcon />
                  </SntActionButton>
                </>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onDataRequestChange = (data, callback) => {
    const { params, dataOptions } = data;
    if (table.current) {
      table.current.setLoading(true);
      searchUIClient.search(params).then(
        ({ data }) => {
          if (table.current) {
            table.current.updateDataTable({
              ...data,
              pageCount: data.countByFilter,
              totalItem: {
                countByFilter: data.countByFilter,
              },
            });
            table.current.setLoading(false);

            const dataCallback = table.current.getDataCallBack(
              params,
              dataOptions,
              data
            );
            callback(dataCallback[0], dataCallback[1]);
          }
          checkToShowResolveAllButton();
        },
        (err) => {
          table.current.setLoading(false);
        }
      );
    }
  };

  const {
    handleOpen,
    handleClose,
    handleOpenError,
    DeleteDialog,
  } = useDeleteAllDialog({
    onDelete: (itemSelected, deleteAll) => {
      wifiCellularMismatchClient
        .delete(itemSelected.id, deleteAll)
        .then(() => {
          handleClose();
          table.current.resetDefault();
        })
        .catch((error) => {
          handleClose();
          let message = "";
          if (
            error &&
            error.response &&
            error.response.data &&
            error.response.data.message
          ) {
            message = error.response.data.message;
          }
          handleOpenError(message);
        });
    },
    allTitle: language.delete_all_mismatches_key,
    language,
  });

  const onDeleteClicked = (el, item) => {
    el.preventDefault();
    handleOpen(
      item,
      item.name &&
        language.delete_message_with_name_key.replace("{0}", item.name)
    );
  };

  const onUpdateDone = () => {
    table.current.refresh();
  };

  const onFilterClicked = (item) => {
    var oldOptionsSettings = sessionStorage.getItem(PAGE_NAME);
    if (oldOptionsSettings) {
      oldOptionsSettings = JSON.parse(oldOptionsSettings);
    }
    oldOptionsSettings.query = [
      {
        categoryId: "info",
        filterKey: "cellId",
        filterType: "MULTI_STRING",
        searchType: "STATIC",
        notFilter: false,
        filterValue: {
          searchText: item.cellId,
        },
      },
      {
        categoryId: "info",
        filterKey: "mcc",
        filterType: "MULTI_STRING",
        searchType: "STATIC",
        notFilter: false,
        filterValue: {
          searchText: item.mcc,
        },
      },
      {
        categoryId: "info",
        filterKey: "net",
        filterType: "MULTI_STRING",
        searchType: "STATIC",
        notFilter: false,
        filterValue: {
          searchText: item.net,
        },
      },
      {
        categoryId: "info",
        filterKey: "area",
        filterType: "MULTI_STRING",
        searchType: "STATIC",
        notFilter: false,
        filterValue: {
          searchText: item.area,
        },
      },
    ];
    sessionStorage.setItem(PAGE_NAME, JSON.stringify(oldOptionsSettings));
    setReloadSessionState((pre) => !pre);
  };

  const checkToShowResolveAllButton = () => {
    //Check if 4 filter cellId,mcc,net existed and have filterValue -> show resolve all button
    var oldOptionsSettings = sessionStorage.getItem(PAGE_NAME);
    if (oldOptionsSettings) {
      oldOptionsSettings = JSON.parse(oldOptionsSettings);
    }
    let listFilters = [];
    for (let i = 0; i <= oldOptionsSettings.query.length; i++) {
      if (
        oldOptionsSettings.query[i]?.filterKey == "cellId" ||
        oldOptionsSettings.query[i]?.filterKey == "mcc" ||
        oldOptionsSettings.query[i]?.filterKey == "net" ||
        oldOptionsSettings.query[i]?.filterKey == "area"
      ) {
        listFilters.push(oldOptionsSettings.query[i]);
      }
    }
    var countFilterValue = 0;
    if (listFilters.length === 4) {
      for (let i = 0; i <= listFilters.length; i++) {
        if (listFilters[i]?.filterValue) {
          countFilterValue++;
        }
      }
      if (countFilterValue == 4) {
        setShowResolveAll(true);
        return;
      }
    }
    setShowResolveAll(false);
  };

  const onResolveAllClick = () => {
    var optionsSettings = sessionStorage.getItem(PAGE_NAME);
    if (optionsSettings) {
      optionsSettings = JSON.parse(optionsSettings);
    }

    let fullFilter = {
      query: optionsSettings.query,
      queryObjectType: "WIFI_CELLULAR_MISMATCH",
    };
    fullFilter.startIndex = null;
    fullFilter.maxResults = null;
    fullFilter.requestedTableColumns = [
      {
        columnKey: "id",
        order: null,
      },
    ];

    searchUIClient
      .search(fullFilter)
      .then(({ data }) => {
        var listMismatch = data.data;
        var results = [];
        for (var i = 0; i < listMismatch.length; i++) {
          results.push(listMismatch[i].id);
        }
        setSelectedItems(results);
        setShowUpdatePopup(true);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (selectedItems?.length) {
      setIsLoadingItems(true);
      // Reset decision state when loading new items
      setDecisionOption(1);
      setNewCellTowerLocation(null);

      wifiCellularMismatchClient
        .getByIds(selectedItems)
        .then((response) => {
          setItems(response.data);
          setIsLoadingItems(false);
        })
        .catch(() => {
          setIsLoadingItems(false);
        });
    }
  }, [selectedItems]);

  const handleDecision = () => {
    if (!items[0]) return;

    setIsUpdating(true);

    switch (decisionOption) {
      case 1: // Delay decision (close dialog without action)
        setIsUpdating(false);
        break;

      case 2: // Keep current tower location (add to private, clear mismatches)
        wifiCellularMismatchClient
          .delete(items[0].id, true, true)
          .then(() => {
            setResponseText(language.update_success_key);
            onUpdateDone();
          })
          .catch((e) => {
            setResponseText(
              language.save_fail + ". " + e.response.data.message
            );
          })
          .finally(() => {
            setIsUpdating(false);
            setShowResponse(true);
          });
        break;

      case 3: // Use new cell tower location (save to private, clear mismatches)
        if (newCellTowerLocation) {
          wifiCellularMismatchClient
            .createPrivateCellularTower(items[0].id, {
              lat: newCellTowerLocation.latitude,
              lng: newCellTowerLocation.longitude,
            })
            .then(() => {
              setResponseText(language.update_success_key);
              onUpdateDone();
            })
            .catch((e) => {
              setResponseText(
                language.save_fail + ". " + e.response.data.message
              );
            })
            .finally(() => {
              setIsUpdating(false);
              setShowResponse(true);
            });
        } else {
          setIsUpdating(false);
          setResponseText(language.please_select_location_on_map_key);
          setShowResponse(true);
        }
        break;

      case 4: // Clear mismatches without adding to private
        wifiCellularMismatchClient
          .delete(items[0].id, true, false)
          .then(() => {
            setResponseText(language.update_success_key);
            onUpdateDone();
          })
          .catch((e) => {
            setResponseText(
              language.save_fail + ". " + e.response.data.message
            );
          })
          .finally(() => {
            setIsUpdating(false);
            setShowResponse(true);
          });
        break;

      default:
        setIsUpdating(false);
        break;
    }

    setShowUpdatePopup(false);
  };

  return (
    <div>
      <DeleteDialog />

      <SntDialog
        isShow={showUpdatePopup}
        onSave={handleDecision}
        onClose={() => setShowUpdatePopup(false)}
        title={language.resolve_wifi_cellular_mismatch_key}
        saveTxt={language.save_key}
        closeTxt={language.cancel_key}
        customModalClassname={"snt-config-column-modal"}
        disabled={isUpdating}
      >
        {() => (
          <>
            {isLoadingItems ? (
              <Loading />
            ) : (
              <div className="row">
                {/* Left Column - Info, Checkboxes, and Decisions */}
                <div className="col-md-4">
                  {/* Info Section */}
                  <SntInfoPanel>
                    {(() => {
                      if (!items || items.length === 0) return null;

                      const locationPoints = items.length;
                      const uniqueMacs = new Set(
                        items.flatMap((item) => item.macs || [])
                      ).size;

                      // Calculate cellular info
                      let cellularCountryInfo = null;
                      let cellularInfo = null;

                      if (items.length > 0) {
                        const firstItem = items[0];
                        if (
                          firstItem.mcc ||
                          firstItem.net ||
                          firstItem.area ||
                          firstItem.cell
                        ) {
                          cellularInfo = GlobalUtils.formatCellularTowerInfo(
                            {
                              mcc: firstItem.mcc,
                              net: firstItem.net,
                              area: firstItem.area,
                              cellId: firstItem.cell,
                            },
                            ""
                          );
                        }
                        if (firstItem.network || firstItem.country) {
                          cellularCountryInfo = `network: ${
                            firstItem.network || language.unknown_key
                          }, country: ${
                            firstItem.country || language.unknown_key
                          }`;
                        }
                      }

                      // Calculate time range
                      let timeRange = null;
                      if (items.length > 0) {
                        const dates = items
                          .map((item) => moment(item.createdAt))
                          .filter((date) => date.isValid());
                        if (dates.length > 0) {
                          const startTime = moment.min(dates);
                          const endTime = moment.max(dates);

                          if (startTime.isSame(endTime)) {
                            // If only one date or all dates are the same, show just one
                            timeRange =
                              language.time_key +
                              `: ${startTime.format("lll")}`;
                          } else {
                            // Show range when there are different dates
                            timeRange =
                              language.time_range_key +
                              `: ${startTime.format("lll")} - ${endTime.format(
                                "lll"
                              )}`;
                          }
                        }
                      }

                      return (
                        <div>
                          {cellularInfo && (
                            <div>
                              <h6>{language.cellular_tower_info}:</h6>
                              <ul>
                                {cellularCountryInfo ? (
                                  <li>{cellularCountryInfo}</li>
                                ) : (
                                  ""
                                )}
                                <li>{cellularInfo}</li>
                              </ul>
                            </div>
                          )}
                          <div>
                            <h6>{language.number_of_cellular_mismatch}:</h6>
                            <ul>
                              <li>
                                {locationPoints} {language.location_points_key},{" "}
                                {uniqueMacs} {language.mac_combinations_key}
                              </li>
                              {timeRange && <li>{timeRange}</li>}
                            </ul>
                          </div>
                        </div>
                      );
                    })()}
                  </SntInfoPanel>

                  {/* Checkbox Section */}
                  <div className="mb-3">
                    <div className="mb-2">
                      <SntCheckBox
                        checked={showWifi}
                        onChange={(value) => setShowWifi(value)}
                        label={language.show_wifi_marker_key}
                      />
                    </div>
                    <div className="mb-2">
                      <SntCheckBox
                        checked={showAdjacentTrustedPoints}
                        onChange={(value) =>
                          setShowAdjacentTrustedPoints(value)
                        }
                        label={language.show_adjacent_trusted_points_key}
                      />
                    </div>
                    <div className="mb-2">
                      <SntCheckBox
                        checked={showLocationiq}
                        onChange={(value) => setShowLocationiq(value)}
                        label={language.show_network_location_locationiq_key}
                      />
                    </div>
                    <div className="mb-2">
                      <SntCheckBox
                        checked={showOpencell}
                        onChange={(value) => setShowOpencell(value)}
                        label={language.show_network_location_opencellid_key}
                      />
                    </div>
                    <div className="mb-2">
                      <SntCheckBox
                        checked={showHere}
                        onChange={(value) => setShowHere(value)}
                        label={language.show_network_location_here_key}
                      />
                    </div>
                  </div>

                  {/* Decision Panel */}
                  <div className="mt-3">
                    <h6>{language.wifi_cellular_mismatch_decision_key}:</h6>
                    <VerticalLayout>
                      <SntRadio
                        value={1}
                        selectedValue={decisionOption}
                        onChange={(value) => setDecisionOption(value)}
                        label={language.delay_decision_dont_know_yet_key}
                      />

                      <SntRadio
                        value={2}
                        selectedValue={decisionOption}
                        onChange={(value) => setDecisionOption(value)}
                        label={language.keep_current_tower_location_key}
                      />

                      <SntRadio
                        value={3}
                        selectedValue={decisionOption}
                        onChange={(value) => setDecisionOption(value)}
                        label={
                          <span>
                            {language.use_new_cell_tower_location_key}
                            {newCellTowerLocation && decisionOption === 3 && (
                              <span className="text-muted ms-2">
                                ({newCellTowerLocation.latitude.toFixed(6)},{" "}
                                {newCellTowerLocation.longitude.toFixed(6)})
                              </span>
                            )}
                            {!newCellTowerLocation && decisionOption === 3 && (
                              <small className="text-warning ms-2">
                                {language.please_select_location_on_map_key}
                              </small>
                            )}
                          </span>
                        }
                      />

                      <SntRadio
                        value={4}
                        selectedValue={decisionOption}
                        onChange={(value) => setDecisionOption(value)}
                        label={language.clear_mismatches_without_adding_key}
                      />
                    </VerticalLayout>
                  </div>
                </div>

                {/* Right Column - Map */}
                <div className="col-md-8">
                  <UpdateWifiCellularMismatchMap
                    items={items}
                    onClick={(data) => {
                      setNewCellTowerLocation({
                        latitude: data.latitude,
                        longitude: data.longitude,
                      });
                      setDecisionOption(3); // Automatically select "use new cell tower location" option
                    }}
                    showWifi={showWifi}
                    setShowWifi={setShowWifi}
                    showLocationiq={showLocationiq}
                    setShowLocationiq={setShowLocationiq}
                    showOpencell={showOpencell}
                    setShowOpencell={setShowOpencell}
                    showHere={showHere}
                    setShowHere={setShowHere}
                    showAdjacentTrustedPoints={showAdjacentTrustedPoints}
                    setShowAdjacentTrustedPoints={setShowAdjacentTrustedPoints}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </SntDialog>

      <SntDialog
        isShow={isShowResponse}
        onSave={() => {
          setShowResponse(false);
        }}
        onClose={() => {
          setShowResponse(false);
        }}
        title={language.notifications_key}
        saveTxt={"OK"}
        isShowCloseButton={false}
        btnSaveClassName="submit btn-sensolus"
      >
        {() => <div>{responseText}</div>}
      </SntDialog>

      <AdminLayout
        headerTitle={language.cell_tower_troubleshooting}
        browserTabTitle={
          language.UserPermission_MENU_ADMIN_CELL_TOWER_TROUBLESHOOTING_description_key
        }
        reloadSessionState={reloadSessionState}
        reload={reload}
        pageName={PAGE_NAME}
        sharedKey={PAGE_NAME}
        defaultOption={DEFAULT_OPTION}
        isAdvanceFilter={true}
        totalLabel={language.items_key}
        body={({ isDisabled, dataOptions, onChangeChildren }) =>
          dataOptions && (
            <>
              {showResolveAll ? (
                <div className="d-flex justify-content-end">
                  <Button
                    className="mb-2"
                    variant="sensolus-greylight"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onResolveAllClick();
                    }}
                  >
                    <SntBulkIcon />
                    <span className="ms-1 d-none d-lg-inline-block">
                      {language.resolve_all_key}
                    </span>
                  </Button>
                </div>
              ) : (
                ""
              )}

              <SntAdvancedSearchTableViewCard
                shared={(child) => (table.current = child)}
                allColumns={allColumns}
                extraCols={extraCols}
                visibleCols={defaultColumnInit}
                disabled={isDisabled}
                onChange={(data) => {
                  onDataRequestChange(data, onChangeChildren);
                }}
                dataOptions={dataOptions}
                isDisabledCopyData={false}
                isDisabbledAddFilter={false}
                onFilterClicked={(action, key, filterKeys, query) => {
                  setForceFilterAction({ action, key, filterKeys, query });
                }}
                filterMap={filterMap}
              />
            </>
          )
        }
        forceFilterAction={forceFilterAction}
        setForceFilterAction={setForceFilterAction}
        onLoadFilter={(data) => setFilterMap(data)}
      ></AdminLayout>
    </div>
  );
};

export default LocationMismatch;
